import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { injectReducer } from 'redux-dynamix';
import EventEmitter from "@scena/event-emitter";

import _ from 'lodash';

import { createEditorSessionReducer } from 'reducers/editorReducer';
import MoveableData from '../helper/moveableData';
import { getWidgetType } from '../helper/widget';

import { PAGE, GROUP, HOTSPOT, MENU, CONTENT_CARD, FORM_STEP, CONTENT, CAROUSEL, TEXT } from 'constants/widgetTypes';

export const registerEditorSessionReducer = (id) => {
  injectReducer(id, createEditorSessionReducer(id));
};

export function useEditorSession(id) {
  const eventBus = new EventEmitter();
  const editorSessionExists = useSelector(state => _.get(state, `${id}`) != null);
  const breakpoint = useSelector(state => _.get(state, `${id}.present.breakpoint`));

  const editorSessionRef = React.useRef({
    id: id,
    moveableData: new MoveableData(id, eventBus, breakpoint),
    moveableManager: React.useRef(null),
    selecto: React.useRef(null),
    eventBus: eventBus
  });

  useEffect(() => {
    editorSessionRef.current = {
      ...editorSessionRef.current,
      id: id, 
      breakpoint: breakpoint
    };

    editorSessionRef.current.moveableData.breakpoint = breakpoint;

    if (!editorSessionExists) {
      registerEditorSessionReducer(id);
    }
  }, [id, breakpoint]);

  return editorSessionRef.current;
}

export function useWidgetSelector(editor, id) {
  var widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  return widgets.find(item => {
    return item.uid === id;
  });
}

// Stable empty selection + cross-hook memo: first useSelectedWidgetSelector call
// per editor state snapshot builds the Map; others reuse the same result reference.
const EMPTY_SELECTED_WIDGETS = [];
const selectedWidgetsByEditorCache = new Map();

export function useSelectedWidgetSelector(editor) {
  return useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);

    if (_.size(selected) == 0) {
      return EMPTY_SELECTED_WIDGETS;
    }

    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const prev = selectedWidgetsByEditorCache.get(editor);
    if (prev && prev.selected === selected && prev.widgets === widgets) {
      return prev.result;
    }

    const widgetMap = new Map(widgets.map(w => [w.uid, w]));
    const result = selected.map(id => widgetMap.get(id)).filter(Boolean);
    selectedWidgetsByEditorCache.set(editor, { selected, widgets, result });
    return result;
  }, shallowEqual);
}

/** Boolean: no widgets selected — shared primitive avoids extra array subscriptions in consumers. */
export function useEmptyWidgetSelection(editor) {
  return useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`) || [];
    return selected.length === 0;
  });
}

// Cached boolean: multi-select where every selected id is a TEXT widget (for batch text styling).
const multiTextOnlySelectionByEditor = new Map();

export function useMultipleTextWidgetsSelected(editor) {
  return useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`) || [];
    if (selected.length <= 1) return false;
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const prev = multiTextOnlySelectionByEditor.get(editor);
    if (prev && prev.selected === selected && prev.widgets === widgets) {
      return prev.result;
    }
    const widgetMap = new Map(widgets.map(w => [w.uid, w]));
    const result = selected.every(uid => widgetMap.get(uid)?.type === TEXT);
    multiTextOnlySelectionByEditor.set(editor, { selected, widgets, result });
    return result;
  });
}

export function useWidgetChildrenSelector(editor, parent) {
  var widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  widgets = widgets.filter(item => {
    return item?.parent === parent;
  });
  return _.orderBy(widgets, 'order', 'desc');
}

export function useInteractiveLiveUrl(editor) {
  var widgets = useWidgetChildrenSelector(editor, null);

  var pageWidget = widgets.find(item => item.type === PAGE && item.order === 1);

  return _.get(pageWidget, 'properties.desktop.alias');
}

/**
 * Return boolean if the widget is selected
 * @param {*} editor 
 * @param {*} parent 
 * @returns 
 */
export function useWidgetSelected(editor, widget) {
  return useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);

    if (_.size(selected) == 0) {
      return false;
    }

    return selected.find(id => id === widget) != null;
  });
}

/*
* Returns a json object indicating if any of the children of the widget is selected & the depth
*/
const EMPTY_CHILDREN_SELECTION = { selected: false, depth: 0 };
const childrenWidgetSelectedCache = new Map();

export function useChildrenWidgetSelected(editor, parent) {
  var widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  var selected = useSelector(state => _.get(state, `${editor}.present.selected`) || []);

  if(_.size(selected) === 0){
    return EMPTY_CHILDREN_SELECTION;
  }

  const cacheKey = `${editor}:${parent}`;
  const prev = childrenWidgetSelectedCache.get(cacheKey);
  if (prev && prev.selected === selected && prev.widgets === widgets) {
    return prev.result;
  }

  const _findWidget = (id) => {
    return widgets.find(item => item.uid === id);
  }

  const _traverseParents = (id, parents) => {
    const widget = _findWidget(id);
    if(widget && widget.parent){
      parents.push(widget.parent);

      if(widget.parent === parent){
        return true;
      }

      return _traverseParents(widget.parent, parents);
    }

    return false;
  }

  var parents = [];
  var found = _traverseParents(selected[0], parents);
  var result = found
    ? { selected: true, depth: _.size(parents) }
    : EMPTY_CHILDREN_SELECTION;

  childrenWidgetSelectedCache.set(cacheKey, { selected, widgets, result });
  return result;
}

/**
 * Return closest container for widget
*/
export function useContainerSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  const item = findWidget(widgets, widget);
  
  if(item && item.parent){
    return findWidget(widgets, item.parent);
  }

  return null;
}

/**
 * Return closest container for widget
*/
export function usePageWidgetSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  

  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if(!item){
      return null;
    }

    const type = _.get(item, 'type');
    if(type === PAGE){
      return item;
    }

    if(item && item.parent){
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

/**
 * Return closest parent for widget
*/
export function useParentWidgetSelectorForType(editor, widget, parentType) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);


  const _findParent = (widgets, widget) => {
    const item = findWidget(widgets, widget);

    if (!item) {
      return null;
    }

    const type = _.get(item, 'type');
    if (type === parentType) {
      return item;
    }

    if (item && item.parent) {
      return _findParent(widgets, item.parent)
    }
  }

  return _findParent(widgets, widget);
}

function findWidget(widgets, widget) {
  return widgets.find(item => item.uid === widget);
}

export default function useIsMounted() {
  const isMounted = React.useRef(false)
  useEffect(() => {
      isMounted.current = true
      return () => {
          isMounted.current = false
      }
  }, [])
  return () => isMounted.current
}


/**
 * Check widget inside Group
*/
export function useGroupContainerSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  const item = findWidget(widgets, widget.uid);

  var parent = null;
  if (item && item.parent) {
    parent =  findWidget(widgets, item.parent);
  }

  return parent && parent.type === GROUP;
}

/**
 * Check widget inside Hotspot
*/
export function useHotspotContainerSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  const item = findWidget(widgets, widget.uid);

  var parent = null;
  if (item && item.parent) {
    parent = findWidget(widgets, item.parent);
  }
  let isCarouselInsideHotspot = false;
  if (parent){
    const topParent = findWidget(widgets, parent.parent);
    if (topParent?.type === CAROUSEL){
      isCarouselInsideHotspot = getWidgetType(editor, topParent.parent) === HOTSPOT;
    }
  }

  return parent && [HOTSPOT, CONTENT_CARD, GROUP, CONTENT].includes(parent.type) ||  isCarouselInsideHotspot;
}

export function useHotspotWidgetSelector(editor, breakpoint) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  
  const hotspot = useSelector(state => {
    return _.get(state, `${editor}.present.hotspot`)
  });

  if(!hotspot) {
    return null;
  }

  return widgets.find(item => item.uid === hotspot && _.get(item, `properties.${breakpoint}.visible`));
}

export function useHotspotParentSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);

  if(!widget){
    return false;
  }

  const _findChildren = (widget) => {
    return widgets.filter(item => item.parent === widget.uid && item.type === HOTSPOT);
  }

  var children = _findChildren(widget);

  if(!children || children.length === 0) {
    return false;
  }

  return true;
}

export function useBrandImageContainerSelector(editor, widget) {
  const widgets = useSelector(state => _.get(state, `${editor}.present.widgets`) || []);
  const item = findWidget(widgets, widget.uid);

  var parent = null;
  if (item && item.parent) {
    parent = findWidget(widgets, item.parent);
  }

  return parent && [CONTENT_CARD, CONTENT].includes(parent.type);
}

/**
 * Check widget can be created as live element
*/
export function useAllowCreateLiveElement(editor, widget) {
  const entityType = useSelector(state => _.get(state, `${editor}.present.entityType`));
  const isGroupContainer = useGroupContainerSelector(editor, widget);
  const isHotspotContainer = useHotspotContainerSelector(editor, widget);
  const isHotspotParent = useHotspotParentSelector(editor, widget);

  return entityType !== 'LIVE_ELEMENT' && !isGroupContainer && !isHotspotContainer && !isHotspotParent;
}

export function isWidgetLocked (allWidgets, widget, breakpoint) {
  if (!widget) return false;

  const locked = _.get(widget, `properties.${breakpoint}.locked`, false);
  if (locked) return true;

  const parent = allWidgets.find(w => w.uid === widget.parent);
  if (!parent) return false;

  return isWidgetLocked(allWidgets, parent, breakpoint);
};

export function useIsWidgetLocked(editor, widget, breakpoint) {
  return useSelector(state => {
    const allWidgets = _.get(state, `${editor}.present.widgets`) || [];
    return isWidgetLocked(allWidgets, widget, breakpoint);
  });
}